import React, { useCallback, useContext, useMemo, useState } from "react";
import { useMessageSource } from "react-message-source";
import FormFullWidthContainer from "../../ui/layout/page/forms-container/form-full-width-container";
import SectionItemContainer from "../../ui/layout/page/forms-container/section-item-container";
import CustomTypography from "../../ui/widgets/typography";
import DashboardBasicAccountApplication from "./basic-account-application";
import DashboardCardholderApplication from "./cardholder-application";
import PageContent from "../../ui/layout/page/content";
import { ProductContext } from "../../context/product-context/productContext";
import ProductFamilyFilter from "./product-family-filter";
import DashboardContextProvider from "./context";
import Info from "../../ui/widgets/info";
import { Product } from "../../context/product-context/product";
import { ProductFamily } from "../../context/product-context/product-family";
import { ProductRefType } from "../../context/product-context/types";
import {
  BasicAccountApplicationDocument,
  DashboardBasicAccountApplicationDocument,
  DashboardCardApplicationDocument,
} from "../../api/commercial/generated-types";
import DashboardHelper from "./helper";
import CustomLink from "../../ui/widgets/link";
import { StartApplicationDialog } from "../shared/components/start-application-dialog/start-application-dialog";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import { CsDisabledInfoDialog } from "./cs-disabled-info-dialog";

export interface DashboardProps {
  basicAccountApplications: DashboardBasicAccountApplicationDocument[];
  cardholderApplications: DashboardCardApplicationDocument[];
  startBasicAccountApplication: (product: ProductRefType) => Promise<void>;
  startCardApplication: (
    basicAccountApplicationRef: string,
    productRef: string,
    companyName: string,
    isSoleProprietorship: boolean,
    hasDecentralizedInvoicing: BasicAccountApplicationDocument["hasDecentralizedInvoicing"],
    limit: number,
  ) => void;
}

export default function Dashboard({
  basicAccountApplications = [],
  cardholderApplications = [],
  startBasicAccountApplication,
  startCardApplication,
}: DashboardProps): React.JSX.Element {
  const [isDialogActive, setIsDialogActive] = useState<boolean>(false);
  const [isCsDisabledDialogActive, setIsCsDisabledDialogActive] =
    useState<boolean>(false);
  useState(false);

  const openCsDisabledDialog = useCallback(
    () => setIsCsDisabledDialogActive(true),
    [],
  );
  const openProductSelector = useCallback(() => {
    setIsDialogActive(true);
  }, []);

  const { selectedProduct, findPersistedProductByRef } =
    useContext(ProductContext);

  // Applied products can be disabled
  const appliedProducts: Product[] = basicAccountApplications.map((applied) =>
    findPersistedProductByRef(applied.productRef as ProductRefType),
  );

  // product family can be changed from UI, do not use context. (hence the separate state)
  const [allProductsForFamily, setAllProductsForFamily] = useState<Product[]>(
    () => {
      return DashboardHelper.getAllProductsFromFamily(
        selectedProduct?.family,
        appliedProducts,
      );
    },
  );

  // Also search from all products, disabled or enabled
  const applicationsFilteredByFamily: DashboardBasicAccountApplicationDocument[] =
    useMemo(() => {
      return basicAccountApplications.filter(
        (application: DashboardBasicAccountApplicationDocument) => {
          const product = findPersistedProductByRef(
            application.productRef as ProductRefType,
          );
          return product.family.name === allProductsForFamily[0].family.name;
        },
      );
    }, [
      basicAccountApplications,
      findPersistedProductByRef,
      allProductsForFamily,
    ]);

  const openProducts = allProductsForFamily.filter(
    (product: Product): boolean => {
      const isProductApplied = appliedProducts.some(
        (appliedProduct: Product) => {
          return product.productRef === appliedProduct.productRef;
        },
      );
      return !isProductApplied;
    },
  );

  const changeProductFamily = (changedFamily: ProductFamily): void => {
    setAllProductsForFamily(
      DashboardHelper.getAllProductsFromFamily(changedFamily, appliedProducts),
    );
  };

  const { getMessage } = useMessageSource();

  return (
    <>
      <StartApplicationDialog
        startBasicAccountApplication={startBasicAccountApplication}
        isActive={isDialogActive}
        setIsActive={setIsDialogActive}
      />
      <CsDisabledInfoDialog
        isActive={isCsDisabledDialogActive}
        setIsActive={setIsCsDisabledDialogActive}
      />
      <PageContent>
        <DashboardContextProvider
          allProductsForFamily={allProductsForFamily}
          appliedProducts={appliedProducts}
        >
          <FormFullWidthContainer>
            <SectionItemContainer>
              <div className="scd-section-helper-lg">
                <CustomTypography className="scd-dashboard-title" variant="h3">
                  {getMessage("Dashboard.header")}
                </CustomTypography>
                <CustomTypography variant="body1">
                  {getMessage("Dashboard.introduction")}
                </CustomTypography>
              </div>
              <div className="scd-section-helper-md">
                <CustomLink
                  onClick={() => {
                    setIsDialogActive(true);
                  }}
                >
                  {getMessage("Dashboard.seeAllProducts.link")}
                </CustomLink>
              </div>
            </SectionItemContainer>
            <SectionItemContainer>
              <ProductFamilyFilter changeProductFamily={changeProductFamily} />
            </SectionItemContainer>
          </FormFullWidthContainer>
          <FormFullWidthContainer>
            <SectionItemContainer>
              <DashboardBasicAccountApplication
                basicAccountApplications={applicationsFilteredByFamily}
                openProducts={openProducts}
                openProductSelector={openProductSelector}
                openCsDisabledDialog={openCsDisabledDialog}
                startBasicAccountApplication={startBasicAccountApplication}
                startCardApplication={startCardApplication}
              />
            </SectionItemContainer>
            <SectionItemContainer>
              <div className="scd-section-helper-lg">
                <CustomTypography variant="h4">
                  {getMessage("Dashboard.cardholderApplication.title")}
                </CustomTypography>
              </div>
            </SectionItemContainer>
            <SectionItemContainer>
              <DashboardCardholderApplication
                cardholderApplications={cardholderApplications}
              />
            </SectionItemContainer>
          </FormFullWidthContainer>
          <FormFullWidthContainer>
            <Info>{getMessage("Dashboard.info.legal.guidance")}</Info>
          </FormFullWidthContainer>
          <FormFullWidthContainer hasAdditionalTopMargin={true}>
            <TypographyItemContainer>
              <CustomTypography variant="h4">
                {getMessage("CardholderConfirmation.application-address")}
              </CustomTypography>
              <CustomTypography variant={"body1"}>
                {getMessage(
                  "CardholderConfirmation.application-address-details-company",
                )}
                ,&nbsp;
                {getMessage(
                  "CardholderConfirmation.application-address-details-street",
                )}
                ,&nbsp;
                {getMessage(
                  "CardholderConfirmation.application-address-details-zip",
                )}
              </CustomTypography>
            </TypographyItemContainer>
          </FormFullWidthContainer>
        </DashboardContextProvider>
      </PageContent>
    </>
  );
}
